import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useHistory} from 'react-router-dom'
import {usePageData} from '../../core'
import {KTSVG} from '../../../helpers'
import {useSelectedProject} from '../../../../app/modules/projectdetails/hooks/useSelectedProject'
import {PAGE_ACTION, PAGE_PERMISSION} from '../../../../app/modules/roles/constant/role-default'
import {listVariationAssessments} from '../../../../app/modules/variationassessments/api'
import {listProgressClaims} from '../../../../app/modules/progressclaim/api'
import {listPcgReports} from '../../../../app/modules/pcgreport/api'
import './GlobalSearchCreate.scss'

type CreateOption = {
  id: string
  title: string
  description: string
  iconPath: string
  onClick: () => void | Promise<void>
  disabled?: boolean
  titleHint?: string
}

type SearchResultItem = {
  id: string
  title: string
  meta?: string
  statusLabel?: string
  statusTone?: string
  route: string
}

type SearchGroupId = 'variation' | 'pcg-report' | 'progress-claim'

type SearchResultGroup = {
  id: SearchGroupId
  title: string
  badgeLabel: string
  badgeClassName: string
  items: SearchResultItem[]
  totalCount: number
  loadingMore?: boolean
  loadMoreError?: boolean
}

type SearchGroupPage = {
  items: SearchResultItem[]
  totalCount: number
}

const SEARCH_MIN_LENGTH = 2
const SEARCH_RESULT_LIMIT = 5
const SEARCH_LOAD_MORE_LIMIT = 10

const searchGroupMeta: Record<
  SearchGroupId,
  Pick<SearchResultGroup, 'title' | 'badgeLabel' | 'badgeClassName'>
> = {
  variation: {
    title: 'Variation',
    badgeLabel: 'Variation',
    badgeClassName: 'global-search-status--warning',
  },
  'pcg-report': {
    title: 'PCG Report',
    badgeLabel: 'PCG Report',
    badgeClassName: 'global-search-status--info',
  },
  'progress-claim': {
    title: 'Progress Claim',
    badgeLabel: 'Progress Claim',
    badgeClassName: 'global-search-status--success',
  },
}

const fetchSearchGroupPage = async (
  groupId: SearchGroupId,
  projectId: string | number,
  term: string,
  skip: number,
  take: number
): Promise<SearchGroupPage> => {
  const query = {projectId, search: term, skip, take, sort: 'updatedAt desc'}

  switch (groupId) {
    case 'variation': {
      const result = await listVariationAssessments(query)
      return {
        totalCount: result.totalCount ?? 0,
        items: (result.items ?? []).map((item) => ({
          id: item.id,
          title: item.description || item.variationNumber,
          meta: item.description ? item.variationNumber : undefined,
          statusLabel: item.statusLabel,
          statusTone: item.statusTone,
          route: `/variation-assessments/${item.id}`,
        })),
      }
    }
    case 'pcg-report': {
      const result = await listPcgReports({...query, includeItems: true})
      return {
        totalCount: result.totalCount ?? 0,
        items: (result.items ?? []).map((item) => ({
          id: item.id,
          title: `PCG Report - ${item.reportNumber}`,
          meta: item.matchedItem
            ? `Matched item: ${item.matchedItem}${
                (item.matchedItemCount ?? 0) > 1 ? ` (+${item.matchedItemCount! - 1} more)` : ''
              }`
            : item.dateOfIssue
            ? `Issue date: ${item.dateOfIssue}`
            : undefined,
          statusLabel: item.statusLabel,
          statusTone: item.statusTone,
          route: `/pcg-report/${item.id}`,
        })),
      }
    }
    case 'progress-claim': {
      const result = await listProgressClaims(query)
      return {
        totalCount: result.totalCount ?? 0,
        items: (result.items ?? []).map((item) => ({
          id: item.id,
          title: `Progress Claim - ${item.claimNumber}`,
          meta: item.dateOfIssue ? `Issue date: ${item.dateOfIssue}` : undefined,
          statusLabel: item.statusLabel,
          statusTone: item.statusTone,
          route: `/progress-claim/${item.id}`,
        })),
      }
    }
  }
}

// Search-specific badge colors: the theme's badge-light-warning/danger/success have too little contrast.
const statusToneClass: Record<string, string> = {
  neutral: 'global-search-status--neutral',
  info: 'global-search-status--info',
  success: 'global-search-status--success',
  warning: 'global-search-status--warning',
  danger: 'global-search-status--danger',
  muted: 'global-search-status--muted',
}

const hasModuleAccess = (permissionSet: Set<string>, permission: PAGE_PERMISSION) =>
  [
    permission,
    `${permission}.${PAGE_ACTION.View}`,
    `${permission}.${PAGE_ACTION.Create}`,
    `${permission}.${PAGE_ACTION.Update}`,
    `${permission}.${PAGE_ACTION.Delete}`,
  ].some((value) => permissionSet.has(value))

const GlobalSearchCreate: React.FC = () => {
  const history = useHistory()
  const {currentUser} = usePageData()
  const {projectId, isMock} = useSelectedProject()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState(false)
  const [searchHasQueried, setSearchHasQueried] = useState(false)
  const [searchGroups, setSearchGroups] = useState<SearchResultGroup[]>([])
  const [hasProgressClaimDraft, setHasProgressClaimDraft] = useState(false)
  const [hasPcgReportDraft, setHasPcgReportDraft] = useState(false)
  const [checkingDraftAvailability, setCheckingDraftAvailability] = useState(false)
  const draftCheckRequestIdRef = useRef(0)
  const searchRequestIdRef = useRef(0)
  const searchTermRef = useRef('')
  const searchContainerRef = useRef<HTMLDivElement | null>(null)

  const hasProject = Boolean(projectId) && !isMock

  const permissionSet = useMemo(
    () => new Set(currentUser?.activePages?.map((page) => page.name) ?? []),
    [currentUser]
  )

  const canCreateProject =
    permissionSet.has(`${PAGE_PERMISSION.Project_SetupWizard}.${PAGE_ACTION.Create}`) ||
    permissionSet.has(`${PAGE_PERMISSION.Project_Details}.${PAGE_ACTION.Create}`)
  const canCreateVariation = permissionSet.has(
    `${PAGE_PERMISSION.Reports_VariationAssessments}.${PAGE_ACTION.Create}`
  )
  const canCreateProgressClaim = permissionSet.has(
    `${PAGE_PERMISSION.Reports_ProgressClaim}.${PAGE_ACTION.Create}`
  )
  const canCreatePcgReport = permissionSet.has(
    `${PAGE_PERMISSION.Reports_PcgReport}.${PAGE_ACTION.Create}`
  )

  const canSearchVariation = hasModuleAccess(
    permissionSet,
    PAGE_PERMISSION.Reports_VariationAssessments
  )
  const canSearchProgressClaim = hasModuleAccess(
    permissionSet,
    PAGE_PERMISSION.Reports_ProgressClaim
  )
  const canSearchPcgReport = hasModuleAccess(permissionSet, PAGE_PERMISSION.Reports_PcgReport)
  const hasSearchableModules = canSearchVariation || canSearchProgressClaim || canSearchPcgReport
  const trimmedSearchTerm = searchTerm.trim()
  const shouldShowSearchDropdown =
    searchFocused &&
    (trimmedSearchTerm.length > 0 ||
      searchLoading ||
      searchHasQueried ||
      !hasProject ||
      !hasSearchableModules)

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 350)
    return () => window.clearTimeout(handle)
  }, [searchTerm])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!searchContainerRef.current?.contains(event.target as Node)) {
        setSearchFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const term = debouncedSearchTerm.trim()

    if (!hasProject || !hasSearchableModules || term.length < SEARCH_MIN_LENGTH) {
      searchRequestIdRef.current += 1
      setSearchGroups([])
      setSearchLoading(false)
      setSearchError(false)
      setSearchHasQueried(false)
      return
    }

    const loadSearchResults = async () => {
      const currentRequest = ++searchRequestIdRef.current
      setSearchLoading(true)
      setSearchError(false)
      setSearchHasQueried(true)

      try {
        const groupIds: SearchGroupId[] = [
          ...(canSearchVariation ? (['variation'] as const) : []),
          ...(canSearchPcgReport ? (['pcg-report'] as const) : []),
          ...(canSearchProgressClaim ? (['progress-claim'] as const) : []),
        ]

        const groups = await Promise.all(
          groupIds.map(async (groupId): Promise<SearchResultGroup> => {
            const page = await fetchSearchGroupPage(
              groupId,
              projectId!,
              term,
              0,
              SEARCH_RESULT_LIMIT
            )
            return {id: groupId, ...searchGroupMeta[groupId], ...page}
          })
        )

        if (currentRequest !== searchRequestIdRef.current) {
          return
        }

        searchTermRef.current = term
        setSearchGroups(groups.filter((group) => group.items.length > 0))
      } catch (error) {
        console.error('Failed to load global search results', error)
        if (currentRequest !== searchRequestIdRef.current) {
          return
        }

        setSearchGroups([])
        setSearchError(true)
      } finally {
        if (currentRequest === searchRequestIdRef.current) {
          setSearchLoading(false)
        }
      }
    }

    void loadSearchResults()
  }, [
    canSearchPcgReport,
    canSearchProgressClaim,
    canSearchVariation,
    debouncedSearchTerm,
    hasProject,
    hasSearchableModules,
    projectId,
  ])

  useEffect(() => {
    if (!showCreateModal || !hasProject) {
      draftCheckRequestIdRef.current += 1
      setHasProgressClaimDraft(false)
      setHasPcgReportDraft(false)
      setCheckingDraftAvailability(false)
      return
    }

    const shouldCheckProgressClaim = canCreateProgressClaim
    const shouldCheckPcgReport = canCreatePcgReport

    if (!shouldCheckProgressClaim && !shouldCheckPcgReport) {
      setHasProgressClaimDraft(false)
      setHasPcgReportDraft(false)
      setCheckingDraftAvailability(false)
      return
    }

    const loadDraftStates = async () => {
      const currentRequest = ++draftCheckRequestIdRef.current
      setCheckingDraftAvailability(true)

      try {
        const [progressClaimResult, pcgResult] = await Promise.all([
          shouldCheckProgressClaim
            ? listProgressClaims({
                projectId: projectId!,
                status: 'draft',
                skip: 0,
                take: 1,
              })
            : Promise.resolve(null),
          shouldCheckPcgReport
            ? listPcgReports({
                projectId: projectId!,
                status: 'draft',
                skip: 0,
                take: 1,
              })
            : Promise.resolve(null),
        ])

        if (currentRequest !== draftCheckRequestIdRef.current) {
          return
        }

        if (shouldCheckProgressClaim) {
          setHasProgressClaimDraft((progressClaimResult?.totalCount ?? 0) > 0)
        }
        if (shouldCheckPcgReport) {
          setHasPcgReportDraft((pcgResult?.totalCount ?? 0) > 0)
        }
      } catch (error) {
        console.error('Failed to evaluate create-option draft availability', error)
        if (currentRequest !== draftCheckRequestIdRef.current) {
          return
        }
        if (shouldCheckProgressClaim) {
          setHasProgressClaimDraft(false)
        }
        if (shouldCheckPcgReport) {
          setHasPcgReportDraft(false)
        }
      } finally {
        if (currentRequest === draftCheckRequestIdRef.current) {
          setCheckingDraftAvailability(false)
        }
      }
    }

    void loadDraftStates()
  }, [canCreatePcgReport, canCreateProgressClaim, hasProject, projectId, showCreateModal])

  const navigateTo = (path: string) => {
    setShowCreateModal(false)
    history.push(path)
  }

  const navigateToSearchResult = (path: string) => {
    setSearchFocused(false)
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSearchGroups([])
    setSearchHasQueried(false)
    history.push(path)
  }

  const updateSearchGroup = (
    groupId: SearchGroupId,
    update: (group: SearchResultGroup) => SearchResultGroup
  ) => {
    setSearchGroups((groups) =>
      groups.map((group) => (group.id === groupId ? update(group) : group))
    )
  }

  const handleLoadMore = async (group: SearchResultGroup) => {
    if (group.loadingMore || !projectId) {
      return
    }

    const requestId = searchRequestIdRef.current
    updateSearchGroup(group.id, (current) => ({
      ...current,
      loadingMore: true,
      loadMoreError: false,
    }))

    try {
      const page = await fetchSearchGroupPage(
        group.id,
        projectId,
        searchTermRef.current,
        group.items.length,
        SEARCH_LOAD_MORE_LIMIT
      )

      if (requestId !== searchRequestIdRef.current) {
        return
      }

      updateSearchGroup(group.id, (current) => {
        const existingIds = new Set(current.items.map((item) => item.id))
        return {
          ...current,
          // A short page means the server has nothing further, even if totalCount drifted.
          totalCount:
            page.items.length < SEARCH_LOAD_MORE_LIMIT
              ? current.items.length + page.items.length
              : page.totalCount,
          items: [...current.items, ...page.items.filter((item) => !existingIds.has(item.id))],
          loadingMore: false,
        }
      })
    } catch (error) {
      console.error(`Failed to load more ${group.id} search results`, error)
      if (requestId !== searchRequestIdRef.current) {
        return
      }

      updateSearchGroup(group.id, (current) => ({
        ...current,
        loadingMore: false,
        loadMoreError: true,
      }))
    }
  }

  const handleProgressClaimCreate = () => {
    navigateTo(hasProject ? '/progress-claim/new' : '/progress-claim')
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSearchGroups([])
    setSearchHasQueried(false)
    setSearchError(false)
  }

  const createOptions: CreateOption[] = []

  if (canCreateVariation) {
    createOptions.push({
      id: 'variation',
      title: 'New Variation',
      description: 'Add a new variation item to document changes.',
      iconPath: '/media/icons/tims/arrow-left-right.svg',
      onClick: () =>
        navigateTo(hasProject ? '/variation-assessments/new' : '/variation-assessments'),
      disabled: !hasProject,
      titleHint: !hasProject ? 'Select a project to create a variation.' : undefined,
    })
  }

  if (canCreateProgressClaim) {
    const progressClaimDisabled = !hasProject || checkingDraftAvailability || hasProgressClaimDraft
    createOptions.push({
      id: 'progress-claim',
      title: 'New Progress Claim Report',
      description: 'Create a new Progress Claim.',
      iconPath: '/media/icons/tims/bars-progress.svg',
      onClick: handleProgressClaimCreate,
      disabled: progressClaimDisabled,
      titleHint: !hasProject
        ? 'Select a project to create a progress claim.'
        : checkingDraftAvailability
        ? 'Checking existing progress claim drafts.'
        : hasProgressClaimDraft
        ? 'A draft progress claim already exists for this project.'
        : undefined,
    })
  }

  if (canCreatePcgReport) {
    const pcgReportDisabled = !hasProject || checkingDraftAvailability || hasPcgReportDraft
    createOptions.push({
      id: 'pcg-report',
      title: 'New PCG Report',
      description: 'Create a new PCG report.',
      iconPath: '/media/icons/tims/menu-reports.svg',
      onClick: () => navigateTo(hasProject ? '/pcg-report/new' : '/pcg-report'),
      disabled: pcgReportDisabled,
      titleHint: !hasProject
        ? 'Select a project to create a PCG report.'
        : checkingDraftAvailability
        ? 'Checking existing PCG report drafts.'
        : hasPcgReportDraft
        ? 'A draft PCG report already exists for this project.'
        : undefined,
    })
  }

  if (canCreateProject) {
    createOptions.push({
      id: 'project',
      title: 'New Project',
      description: 'Create a new project to organize your work and track progress.',
      iconPath: '/media/icons/tims/menu-product.svg',
      onClick: () => navigateTo('/project-setup'),
    })
  }

  const renderSearchDropdownContent = () => {
    if (!hasProject) {
      return <div className='global-search-empty'>Select a project to search reports.</div>
    }

    if (!hasSearchableModules) {
      return <div className='global-search-empty'>No searchable modules available.</div>
    }

    if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < SEARCH_MIN_LENGTH) {
      return (
        <div className='global-search-empty'>Enter at least {SEARCH_MIN_LENGTH} characters.</div>
      )
    }

    if (searchLoading) {
      return (
        <div className='global-search-empty'>
          <span className='spinner-border spinner-border-sm text-gray-500 me-2'></span>
          Searching...
        </div>
      )
    }

    if (searchError) {
      return <div className='global-search-empty text-danger'>Search failed. Try again.</div>
    }

    if (searchHasQueried && searchGroups.length === 0) {
      return <div className='global-search-empty'>No results found.</div>
    }

    return (
      <div className='global-search-results__groups'>
        {searchGroups.map((group) => {
          const remainingCount = Math.max(group.totalCount - group.items.length, 0)

          return (
            <div key={group.id} className='global-search-results__group'>
              <div className='global-search-results__group-title'>
                {group.title}{' '}
                <span className='global-search-results__group-count'>({group.totalCount})</span>
              </div>
              {group.items.map((item) => (
                <button
                  key={`${group.id}-${item.id}`}
                  type='button'
                  className='global-search-results__item'
                  onClick={() => navigateToSearchResult(item.route)}
                >
                  <span className='global-search-results__item-content'>
                    <span className='global-search-results__item-title' title={item.title}>
                      {item.title}
                    </span>
                    {item.meta && (
                      <span className='global-search-results__item-meta' title={item.meta}>
                        {item.meta}
                      </span>
                    )}
                  </span>
                  <span
                    className={`badge ${
                      item.statusTone
                        ? statusToneClass[item.statusTone] ?? group.badgeClassName
                        : group.badgeClassName
                    }`}
                  >
                    {item.statusLabel || group.badgeLabel}
                  </span>
                </button>
              ))}
              {remainingCount > 0 && (
                <button
                  type='button'
                  className='global-search-results__load-more'
                  onClick={() => void handleLoadMore(group)}
                  disabled={group.loadingMore}
                >
                  {group.loadingMore ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Loading...
                    </>
                  ) : group.loadMoreError ? (
                    'Failed to load. Try again'
                  ) : (
                    <>
                      Show {Math.min(remainingCount, SEARCH_LOAD_MORE_LIMIT)} more
                      <i className='bi bi-chevron-down ms-1'></i>
                    </>
                  )}
                </button>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <div className='header-global-actions d-flex align-items-center justify-content-center gap-3 flex-grow-1'>
        <div className='header-global-search' ref={searchContainerRef}>
          <div className='input-group input-group-solid global-search-input-group'>
            <span className='input-group-text'>
              <i className='bi bi-search'></i>
            </span>
            <input
              type='text'
              className='form-control global-search-input'
              placeholder={hasProject ? 'Search...' : 'Select project to search...'}
              value={searchTerm}
              onFocus={() => setSearchFocused(true)}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setSearchFocused(false)
                }
              }}
            />
            {searchTerm && (
              <button
                type='button'
                className='btn btn-icon global-search-clear'
                onClick={handleClearSearch}
                aria-label='Clear search'
              >
                <i className='bi bi-x-lg'></i>
              </button>
            )}
          </div>

          {shouldShowSearchDropdown && (
            <div className='global-search-results'>{renderSearchDropdownContent()}</div>
          )}
        </div>

        <button
          type='button'
          className='btn btn-dark header-global-create'
          onClick={() => setShowCreateModal(true)}
        >
          <i className='bi bi-plus-lg me-2'></i>
          Create
        </button>
      </div>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton placeholder=''>
          <Modal.Title>Create New</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createOptions.length === 0 ? (
            <div className='text-muted'>No create options available.</div>
          ) : (
            <div>
              {createOptions.map((option) => (
                <button
                  key={option.id}
                  type='button'
                  className='global-create-option'
                  onClick={option.onClick}
                  disabled={option.disabled}
                  title={option.titleHint}
                >
                  <span className='global-create-option__icon'>
                    <KTSVG path={option.iconPath} className='svg-icon-2' />
                  </span>
                  <span className='global-create-option__content'>
                    <span className='global-create-option__title'>{option.title}</span>
                    <span className='global-create-option__description'>{option.description}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export {GlobalSearchCreate}
